import React from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,

  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  DoneAll,
  ErrorOutline,
  FiberManualRecord,
  NotificationsNone,
  PlayCircleOutline,
  PowerOff,
  PowerSettingsNew,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { AppNotification, NotificationType } from '../types';

const typeConfig: Record<NotificationType, { icon: React.ReactNode; color: string }> = {
  machine_offline: { icon: <PowerOff fontSize="small" />, color: '#EF4444' },
  machine_online: { icon: <PowerSettingsNew fontSize="small" />, color: '#22C55E' },
  production_complete: { icon: <CheckCircleOutline fontSize="small" />, color: '#02B68D' },
  production_start: { icon: <PlayCircleOutline fontSize="small" />, color: '#4F7CFF' },
  error: { icon: <ErrorOutline fontSize="small" />, color: '#EF4444' },
  message: { icon: <NotificationsNone fontSize="small" />, color: '#22A6F2' },
  warning: { icon: <Warning fontSize="small" />, color: '#FFB454' },
};

const formatRelativeTime = (timestamp: string): string => {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Adesso';
  if (mins < 60) return `${mins} min fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  return `${days}g fa`;
};

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ anchorEl, onClose }) => {
  const { state, markNotificationRead, markAllNotificationsRead } = useApp();
  const navigate = useNavigate();
  const notifications = state.notifications.slice(0, 20);
  const unreadCount = state.notifications.filter((n) => !n.read).length;

  const handleClick = (notif: AppNotification) => {
    markNotificationRead(notif.id);
    if (notif.machineId) {
      navigate(`/machines/${notif.machineId}`);
    }
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: 'min(380px, calc(100vw - 24px))',
            maxHeight: 480,
            mt: 1,
            borderRadius: 3,
            background: 'var(--menu-bg)',
            border: '1px solid var(--border-color)',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
          },
        },
      }}
    >
      <Box sx={{ px: 2.2, py: 1.6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Notifiche
          </Typography>
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.68rem' } }} />
          )}
        </Box>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<DoneAll sx={{ fontSize: 16 }} />}
            onClick={markAllNotificationsRead}
            sx={{ textTransform: 'none', fontSize: '0.78rem', color: 'var(--accent-cyan)' }}
          >
            Segna tutte lette
          </Button>
        )}
      </Box>

      <Divider sx={{ borderColor: 'divider' }} />

      {notifications.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <NotificationsNone sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Nessuna notifica
          </Typography>
        </Box>
      ) : (
        <List sx={{ py: 0, maxHeight: 340, overflow: 'auto' }}>
          {notifications.map((notif) => {
            const config = typeConfig[notif.type] || typeConfig.message;
            return (
              <ListItemButton
                key={notif.id}
                onClick={() => handleClick(notif)}
                sx={{
                  px: 2,
                  py: 1.2,
                  backgroundColor: notif.read ? 'transparent' : 'rgba(25, 198, 183, 0.04)',
                  borderLeft: notif.read ? '3px solid transparent' : `3px solid ${config.color}`,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: config.color }}>
                  {config.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Typography variant="body2" sx={{ fontWeight: notif.read ? 500 : 700, fontSize: '0.85rem' }}>
                        {notif.title}
                      </Typography>
                      {!notif.read && (
                        <FiberManualRecord sx={{ fontSize: 8, color: config.color }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: '70%' }} noWrap>
                        {notif.body}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', flexShrink: 0 }}>
                        {formatRelativeTime(notif.timestamp)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      )}

      <Divider sx={{ borderColor: 'divider' }} />
      <Box sx={{ px: 2, py: 1.2, textAlign: 'center' }}>
        <Button
          size="small"
          onClick={() => {
            navigate('/messages');
            onClose();
          }}
          sx={{ textTransform: 'none', fontSize: '0.82rem', color: 'var(--accent-cyan)' }}
        >
          Vedi tutti i messaggi
        </Button>
      </Box>
    </Popover>
  );
};

export default NotificationPanel;
